import { LANDSCAPE_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/landscapeBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { multiPlaneParallax, biomeCloser } from '../presetBuilders';

/** Arctic (frozen lake / glacier) — cold + still: pale snow motes, cold floor glow, a very-slow
 *  light bed, falling wind-blown snow, drifting fog, and distant ice peaks. */
export const ARCTIC: AtmospherePreset = {
  id: 'arctic',
  layers: [
    // Lit cold sky (PX3) — night-blue top → pale frost horizon — so the ice peaks read.
    { kind: 'skyGradient', from: 0x0a1420, to: 0x44637e },
    // Pale moon high over the frozen lake — a cold focal sky element (reuses the
    // celestial kind; the arena uses a warm sun).
    { kind: 'celestial', atX: 0.72, atY: 0.15, color: 0xeaf4ff, haloColor: 0xbcd6ee, size: 110, haloScale: 2.4, coreAlpha: 0.55, haloAlpha: 0.16 },
    // Pale, slow moonlit clouds drifting high across the moon (sky band, behind
    // the peaks). Soft-light is ADDITIVE — a bright pale tint reads as a moonlit
    // cloud over the dark night sky.
    {
      kind: 'cloudBands', count: 5, color: 0xaecbe8,
      sizeMin: 280, sizeMax: 520, alphaMin: 0.05, alphaMax: 0.14,
      driftPxPerSec: 4, bandYMin: 0.04, bandYMax: 0.22,
    },
    // Aurora borealis over the frozen lake — dancing VERTICAL green/cyan/violet light
    // curtains hanging from the night sky (tall ray_ribbon shafts; sway + lean + shimmer).
    {
      kind: 'aurora', colors: [0x33ffaa, 0x00ffcc, 0x66ffe0, 0x9a7cff],
      bands: 5, widthMin: 32, widthMax: 68, heightMin: 180, heightMax: 320,
      alphaMin: 0.08, alphaMax: 0.22, bandYMin: 0.0, bandYMax: 0.04,
      swayPx: 26, periodMinMs: 5500, periodMaxMs: 10000, driftPxPerSec: 4,
    },
    {
      kind: 'fireflies', textureKey: 'sparkle', // ice glints twinkle
      count: 18, colors: [P.ice, P.frostCore, P.cyan],
      sizeMin: 5, sizeMax: 12, driftPx: 26,
      periodMinMs: 9000, periodMaxMs: 17000, alphaMin: 0.2, alphaMax: 0.6,
    },
    {
      kind: 'glowBlobs',
      blobs: [
        { color: P.frostCore, atX: 0.5, atY: 0.95, size: 540, alpha: 0.08 },
        { color: P.ice, atX: 0.5, atY: 0.06, size: 480, alpha: 0.07 },
      ],
    },
    {
      kind: 'lightField',
      count: 14, palette: [P.ice, P.frostCore, P.cyan],
      sizeMin: 90, sizeMax: 220, alphaMin: 0.04, alphaMax: 0.15,
      driftRadius: 50, periodMinMs: 12000, periodMaxMs: 20000,
    },
    {
      kind: 'weather', mode: 'fall', color: 0xeaf4ff,
      size: 14, frequencyMs: 90, lifespanMs: 4200, speed: 30, windX: 40, alphaEnd: 0.55,
    },
    {
      kind: 'fog', color: P.ice, bands: 5,
      alphaMin: 0.04, alphaMax: 0.14, driftPx: 70, periodMinMs: 14000, periodMaxMs: 24000,
    },
    // Multi-plane ice depth (PX3): far peaks fade into the cold haze → near = dark slate.
    // All bands sit on the scene horizon (y=0 lift, scene-schema); far = TALLEST snowcaps
    // behind, near = SHORTEST foothills in front.
    ...multiPlaneParallax([
      { svg: LANDSCAPE_BACKDROPS.arctic.far, shape: 'peaks', color: 0x4a6076, speed: 0.04, height: 420, alpha: 0.72,
        detail: 0.9, sway: { ampPx: 12, periodMs: 30000 } },
      { svg: LANDSCAPE_BACKDROPS.arctic.mid, shape: 'peaks', color: 0x32465a, speed: 0.10, height: 300, alpha: 0.84,
        detail: 1.1 },
      { svg: LANDSCAPE_BACKDROPS.arctic.near, shape: 'peaks', color: 0x1e2c3a, speed: 0.18, height: 200, alpha: 0.94,
        detail: 1 },
    ]),
    ...biomeCloser({
      // Final colour-grade — pale cyan-white overhead cooling toward the floor.
      grade: { top: 0xcfe9ff, bottom: 0x5d7da8, strength: 0.12 },
    }),
  ],
};
